"use client";

import { useState } from "react";
import { AdminDashboardLayout } from "./admin-dashboard-layout";
import { AdminPlatformOverview } from "./platform-overview";
import { SystemHealthMetrics } from "./system-health-metrics";
import { FinancialOversightEnhanced } from "./financial-oversight-enhanced";
import { TransactionCharts } from "./transaction-charts";
import { AdminMobileActions } from "./mobile/admin-mobile-actions";
import { useDashboardData } from "@/hooks/admin/use-dashboard-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";
import { Button } from "@/ui/components/primitives/button";
import { RefreshCw, Download, Settings } from "lucide-react";
import { Skeleton } from "@/ui/components/primitives/skeleton";

export function EnhancedAdminDashboard() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data, loading, error, lastUpdated, refresh } = useDashboardData(30000);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (error) {
    return (
      <AdminDashboardLayout
        title="Admin Dashboard"
        subtitle="Error loading dashboard data"
        showLiveIndicator={false}
      >
        <Card className="admin-card-plain">
          <CardContent className="admin-card-body">
            <div className="text-center py-8">
              <div className="admin-text-muted mb-4">Failed to load dashboard data</div>
              <Button onClick={handleRefresh} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </AdminDashboardLayout>
    );
  }

  if (loading || !data) {
    return (
      <AdminDashboardLayout
        title="Admin Dashboard"
        subtitle="Loading platform overview..."
        showLiveIndicator={false}
      >
        <div className="space-y-6">
          {/* Skeleton for platform overview cards */}
          <div className="admin-grid admin-grid-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="admin-card">
                <CardHeader className="admin-card-header">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent className="admin-card-body">
                  <Skeleton className="h-8 w-20 mb-2" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Skeleton for system health */}
          <div className="admin-grid admin-grid-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="admin-card-plain">
                <CardHeader className="admin-card-header">
                  <Skeleton className="h-4 w-20" />
                </CardHeader>
                <CardContent className="admin-card-body">
                  <div className="space-y-3">
                    {[1, 2, 3].map((j) => (
                      <div key={j}>
                        <Skeleton className="h-3 w-16 mb-1" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AdminDashboardLayout>
    );
  }

  return (
    <>
      <AdminDashboardLayout
        title="Platform Overview"
        subtitle="Real-time view of merchants, volume, and system health across the platform."
      >
        <div className="space-y-6">
          {/* Platform Overview */}
          <AdminPlatformOverview
            data={{
              ...data.platformOverview,
              dailyRevenue: data.financialOversight.platformRevenue.dailyRevenue
            }}
            loading={loading}
          />

          {/* System Health Metrics */}
          <SystemHealthMetrics data={data.systemHealth} />

          {/* Financial Oversight */}
          <FinancialOversightEnhanced data={data.financialOversight} />

          {/* Transaction Charts */}
          <TransactionCharts initialData={data.dailyTransactionData} />
        </div>
      </AdminDashboardLayout>

      {/* Mobile Actions */}
      <AdminMobileActions
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        notificationCount={2}
      />
    </>
  );
}